// Provider client for DeepSeek (OpenAI-compatible) and Gemini (native
// generateContent REST API). All game LLM traffic goes through chatComplete.
// ALL game LLM traffic goes through chatComplete so queueing + tests share
// one path. Verified against docs (Sept 2026):
// - model `deepseek-flash`, baseURL https://api.deepseek.com
// - response_format {type:'json_object'} + the word "JSON" in the prompt
// - max_tokens (not max_completion_tokens)
// - Thinking mode is ON by default (effort high): it silently ignores
//   `temperature` and bills extra reasoning tokens per call. This game wants
//   fast, cheap, temperature-steered chatter, so every call explicitly sends
//   thinking.type=disabled (Thinking Mode guide). The JS SDK has no
//   extra_body option (that's Python), so we attach `thinking` inline in the
//   body object — buildRequest serializes the whole body as JSON.
// - Browser-direct calls work: preflight echoes our origin with
//   `access-control-allow-origin` (verified via curl OPTIONS).
import OpenAI from 'openai';
import { TUNING } from '../constants.js';
import { isHostedSite } from './hosted.js';

export const DEEPSEEK_BASE_URL = 'https://api.deepseek.com';
export const DEFAULT_MODEL = 'deepseek-flash';
export const GEMINI_BASE_URL = 'https://generativelanguage.googleapis.com/v1beta/models';
export const DEFAULT_GEMINI_MODEL = 'gemini-3.5-flash-lite';
// Gemini key slots in Setup. Rotation only ever touches filled slots.
export const GEMINI_MAX_KEYS = 5;
export type LlmProvider = 'deepseek' | 'gemini';

export interface LlmConfig {
  provider: LlmProvider;
  apiKey: string;
  apiKeys: string[];
  model: string;
}

// Game-wide thinking policy: off for speed/cost on the hot path.
// Flip to { type: 'enabled' } if you want deeper reasoning (slower, pricier,
// and `temperature` will be ignored — see Thinking Mode guide).
export const THINKING = { type: 'disabled' } as const;
// Attach DeepSeek's thinking toggle to a chat-completions body.
// The JS SDK type doesn't know this field, so callers cast at the boundary.
export function withThinking<T extends object>(
  body: T,
): T & { thinking: typeof THINKING } {
  return { ...body, thinking: THINKING };
}

let client: OpenAI | null = null;
let provider: LlmProvider = 'deepseek';
let modelOverride: string = DEFAULT_MODEL;
// Key ring: DeepSeek configures exactly one; Gemini up to GEMINI_MAX_KEYS.
// Only non-blank entries participate in rotation.
let apiKeys: string[] = [];
// Rotation cursor into apiKeys. Advanced on transport failure; sticky on
// success so a healthy key isn't abandoned mid-session.
let keyCursor = 0;
let fetchOverride: typeof fetch | undefined;

function activeKeys(): string[] {
  return apiKeys.map((k) => k.trim()).filter(Boolean);
}

export function configureLlm(
  apiKeyOrProvider: string | string[] | LlmProvider,
  modelOrKey?: string | string[],
  fetchOrModel?: typeof fetch | string,
  maybeFetch?: typeof fetch,
): void {
  // Legacy call shape remains supported for existing callers/tests:
  // configureLlm(apiKey, model, fetch). New shapes are
  // configureLlm(provider, apiKey, model, fetch) and
  // configureLlm(provider, string[], model, fetch).
  const modern = apiKeyOrProvider === 'deepseek' || apiKeyOrProvider === 'gemini';
  provider = modern ? apiKeyOrProvider : 'deepseek';
  const keyInput = modern ? modelOrKey ?? '' : apiKeyOrProvider;
  const keys = Array.isArray(keyInput) ? keyInput : [keyInput];
  const model = modern ? (typeof fetchOrModel === 'string' ? fetchOrModel : undefined) : (modelOrKey as string | undefined);
  const fetchImpl = (modern ? maybeFetch : fetchOrModel) as typeof fetch | undefined;
  apiKeys = keys.map((k) => k.trim()).filter(Boolean);
  keyCursor = 0;
  fetchOverride = fetchImpl;
  modelOverride = model?.trim() || (provider === 'gemini' ? DEFAULT_GEMINI_MODEL : DEFAULT_MODEL);
  client = provider === 'deepseek'
    ? new OpenAI({
        apiKey: apiKeys[0] ?? '',
        baseURL: DEEPSEEK_BASE_URL,
        dangerouslyAllowBrowser: true,
        ...(fetchImpl ? { fetch: fetchImpl } : {}),
      })
    : null;
}

export function currentModel(): string {
  return modelOverride;
}

export function currentProvider(): LlmProvider {
  return provider;
}

export function currentConfig(): LlmConfig {
  return { provider, apiKey: apiKeys[0] ?? '', apiKeys: [...apiKeys], model: modelOverride };
}

export function deepSeekReady(): boolean {
  return isHostedSite() || currentConfig().apiKeys.length > 0;
}

// Which ring slot will be tried first on the next Gemini call (for tests/UI).
export function currentKeyIndex(): number {
  const keys = activeKeys();
  return keys.length === 0 ? 0 : keyCursor % keys.length;
}

// Transport vs content failures must stay distinct: a 401/timeout/empty
// body means the LLM is unreachable, but a 200 with unparseable text means
// it answered and our parser rejected it. Only transport errors may trip
// the UI's offline pill (see useConversations llmDown).
export class LlmTransportError extends Error {
  constructor(message: string, opts?: { cause?: unknown }) {
    super(message, opts);
    this.name = 'LlmTransportError';
  }
}

function requireClient(): OpenAI {
  if (!client) throw new Error('LLM not configured: set API key in Setup first.');
  return client;
}

// --- Session tally: every LLM call + token usage, for the discreet meter ---
export interface SessionTally {
  calls: number;
  promptTokens: number;
  completionTokens: number;
}

let tally: SessionTally = { calls: 0, promptTokens: 0, completionTokens: 0 };
const tallyListeners = new Set<() => void>();

// Stable snapshot identity between notifications (useSyncExternalStore-safe).
export function getTally(): SessionTally {
  return tally;
}

export function subscribeTally(fn: () => void): () => void {
  tallyListeners.add(fn);
  return () => {
    tallyListeners.delete(fn);
  };
}

export function resetTally(): void {
  tally = { calls: 0, promptTokens: 0, completionTokens: 0 };
  tallyListeners.forEach((l) => l());
}

function recordUsage(prompt: number, completion: number): void {
  tally = {
    calls: tally.calls + 1,
    promptTokens: tally.promptTokens + prompt,
    completionTokens: tally.completionTokens + completion,
  };
  tallyListeners.forEach((l) => l());
}

// Simple semaphore: max N concurrent dialogue generations.
class Semaphore {
  private running = 0;
  private queue: Array<() => void> = [];
  constructor(private max: number) {}
  async run<T>(fn: () => Promise<T>): Promise<T> {
    if (this.running >= this.max) {
      await new Promise<void>((res) => this.queue.push(res));
    }
    this.running++;
    try {
      return await fn();
    } finally {
      this.running--;
      this.queue.shift()?.();
    }
  }
}

export const dialogueGenQueue = new Semaphore(TUNING.maxConcurrentDialogueGen);

export async function chatComplete(
  messages: OpenAI.Chat.ChatCompletionMessageParam[],
  opts?: { json?: boolean; maxTokens?: number; temperature?: number; model?: string },
): Promise<string> {
  if (isHostedSite()) {
    let response: Response;
    try {
      response = await (fetchOverride ?? fetch)('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages,
          ...(opts?.json !== undefined ? { json: opts.json } : {}),
          ...(opts?.maxTokens !== undefined ? { maxTokens: opts.maxTokens } : {}),
          ...(opts?.temperature !== undefined ? { temperature: opts.temperature } : {}),
          model: opts?.model ?? modelOverride,
        }),
      });
    } catch (e) {
      throw new LlmTransportError('Hosted LLM request failed', { cause: e });
    }
    if (!response.ok) {
      throw new LlmTransportError(`Hosted LLM request failed (HTTP ${response.status})`);
    }
    let data: { text?: unknown; promptTokens?: unknown; completionTokens?: unknown };
    try {
      data = await response.json() as typeof data;
    } catch (e) {
      throw new LlmTransportError('Hosted LLM returned invalid JSON', { cause: e });
    }
    const text = typeof data.text === 'string' ? data.text : '';
    recordUsage(
      typeof data.promptTokens === 'number' ? data.promptTokens : 0,
      typeof data.completionTokens === 'number' ? data.completionTokens : 0,
    );
    if (!text.trim()) throw new LlmTransportError('Empty hosted LLM response');
    return text;
  }
  if (provider === 'gemini') return geminiComplete(messages, opts);
  const c = requireClient();
  let res;
  try {
    res = await c.chat.completions.create(
      withThinking({
        model: modelOverride,
        messages,
        stream: false,
        temperature: opts?.temperature ?? 0.9,
        max_tokens: opts?.maxTokens ?? 2500,
        ...(opts?.json ? { response_format: { type: 'json_object' as const } } : {}),
      }) as OpenAI.Chat.ChatCompletionCreateParamsNonStreaming,
    );
  } catch (e) {
    throw new LlmTransportError('LLM request failed (network/key/rate-limit?)', { cause: e });
  }
  // JSON mode occasionally returns empty content (per docs) — treat as
  // transport failure so callers hit offline fallbacks instead of parsing ''.
  const text = res.choices[0]?.message?.content ?? '';
  recordUsage(res.usage?.prompt_tokens ?? 0, res.usage?.completion_tokens ?? 0);
  if (!text.trim()) throw new LlmTransportError('Empty LLM response');
  return text;
}

function geminiText(messages: OpenAI.Chat.ChatCompletionMessageParam[]): string {
  return messages.map((message) => {
    const content = typeof message.content === 'string' ? message.content : JSON.stringify(message.content);
    return `${message.role.toUpperCase()}\n${content}`;
  }).join('\n\n');
}

async function geminiAttempt(
  apiKey: string,
  messages: OpenAI.Chat.ChatCompletionMessageParam[],
  opts?: { json?: boolean; maxTokens?: number; temperature?: number },
): Promise<{ text: string; prompt: number; completion: number }> {
  let response: Response;
  try {
    response = await (fetchOverride ?? fetch)(`${GEMINI_BASE_URL}/${encodeURIComponent(modelOverride)}:generateContent`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-goog-api-key': apiKey },
      body: JSON.stringify({
        contents: [{ role: 'user', parts: [{ text: geminiText(messages) }] }],
        generationConfig: {
          ...(opts?.json ? { responseMimeType: 'application/json' } : {}),
          ...(opts?.maxTokens ? { maxOutputTokens: opts.maxTokens } : {}),
          ...(opts?.temperature !== undefined ? { temperature: opts.temperature } : {}),
        },
      }),
    });
  } catch (e) {
    throw new LlmTransportError('Gemini request failed (network/key/rate-limit?)', { cause: e });
  }
  if (!response.ok) throw new LlmTransportError(`Gemini request failed (HTTP ${response.status})`);
  const data = await response.json() as {
    candidates?: Array<{ content?: { parts?: Array<{ text?: string }> } }>;
    usageMetadata?: { promptTokenCount?: number; candidatesTokenCount?: number };
  };
  const text = data.candidates?.[0]?.content?.parts?.map((part) => part.text ?? '').join('') ?? '';
  if (!text.trim()) throw new LlmTransportError('Empty Gemini response');
  return {
    text,
    prompt: data.usageMetadata?.promptTokenCount ?? 0,
    completion: data.usageMetadata?.candidatesTokenCount ?? 0,
  };
}

async function geminiComplete(
  messages: OpenAI.Chat.ChatCompletionMessageParam[],
  opts?: { json?: boolean; maxTokens?: number; temperature?: number },
): Promise<string> {
  const keys = activeKeys();
  if (keys.length === 0) throw new LlmTransportError('Gemini not configured: set API key in Setup first.');
  // Failover ring: try the cursor key first, then cycle through the rest,
  // wrapping back to the first. Only transport failures rotate; a good
  // response keeps the cursor where it is (sticky on success).
  const start = keys.length === 0 ? 0 : keyCursor % keys.length;
  let lastError: unknown = null;
  for (let attempt = 0; attempt < keys.length; attempt++) {
    const idx = (start + attempt) % keys.length;
    try {
      const res = await geminiAttempt(keys[idx], messages, opts);
      recordUsage(res.prompt, res.completion);
      return res.text;
    } catch (e) {
      lastError = e;
      keyCursor = (idx + 1) % keys.length;
    }
  }
  throw lastError instanceof Error ? lastError : new LlmTransportError('Gemini request failed');
}

// Cheap connectivity check for Setup screen (tiny ping).
export async function pingApiKey(
  apiKey: string,
  model?: string,
  fetchImpl?: typeof fetch,
  providerArg: LlmProvider = 'deepseek',
): Promise<boolean> {
  if (providerArg === 'gemini') {
    try {
      const response = await (fetchImpl ?? fetch)(`${GEMINI_BASE_URL}/${encodeURIComponent(model?.trim() || DEFAULT_GEMINI_MODEL)}:generateContent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-goog-api-key': apiKey },
        body: JSON.stringify({
          contents: [{ parts: [{ text: 'Reply with exactly: ok' }] }],
          generationConfig: { maxOutputTokens: 5 },
        }),
      });
      if (!response.ok) return false;
      const data = await response.json() as { usageMetadata?: { promptTokenCount?: number; candidatesTokenCount?: number } };
      recordUsage(data.usageMetadata?.promptTokenCount ?? 0, data.usageMetadata?.candidatesTokenCount ?? 0);
      return true;
    } catch {
      return false;
    }
  }
  try {
    const c = new OpenAI({
      apiKey,
      baseURL: DEEPSEEK_BASE_URL,
      dangerouslyAllowBrowser: true,
      ...(fetchImpl ? { fetch: fetchImpl } : {}),
    });
    const res = await c.chat.completions.create(
      withThinking({
        model: model?.trim() || DEFAULT_MODEL,
        messages: [{ role: 'user', content: 'Reply with exactly: ok' }],
        max_tokens: 5,
        stream: false,
      }) as OpenAI.Chat.ChatCompletionCreateParamsNonStreaming,
    );
    recordUsage(res.usage?.prompt_tokens ?? 0, res.usage?.completion_tokens ?? 0);
    return true;
  } catch {
    return false;
  }
}
